// Shared verse-rendering helpers for module 9 (recording) — used by both the
// Learn-side continuous-verse display and the Test-side recording screen, so
// the two stay visually identical.

const PAUSE_MARKS = "ۖۗۘۙۚۛۜ";
const SHADDAH = "ّ";
const SUKOON = "ْ";

// Waqf/pause marks, ayah-end circles, and punctuation — stripping these
// leaves just the base Arabic letters of a word. A token that strips down
// to nothing was never a real word to begin with (a standalone stop/end
// sign), so it's excluded from "words" lists (Sight Vocabulary, New Words
// playback) entirely rather than shown as a blank/decoration-only entry.
const DECORATIONS_RE = /[۝﴾﴿،۔ۚۖۗۘۙۛۜ٪ج لاصزطفقع]+/g;
export function stripDecorations(tok) {
  return (tok || "").replace(DECORATIONS_RE, "");
}
export function isProperWord(tok) {
  return stripDecorations(tok).length > 0;
}

// Lightweight Tajweed-style highlighter — colors the optional pause marks
// and shaddah so students can navigate the verse at a glance. Not a full
// Tajweed parser; a visual aid only.
export function tajweedColor(text) {
  if (!text) return text;
  const parts = [];
  let buf = "";
  const flush = () => { if (buf) { parts.push(buf); buf = ""; } };
  for (const ch of text) {
    if (PAUSE_MARKS.includes(ch)) {
      flush();
      parts.push(<span key={`p${parts.length}`} style={{ color: "#93C5FD", fontWeight: 700 }}>{ch}</span>);
    } else if (ch === SHADDAH) {
      flush();
      parts.push(<span key={`s${parts.length}`} style={{ color: "#FCD34D", fontWeight: 700 }}>{ch}</span>);
    } else if (ch === SUKOON) {
      flush();
      parts.push(<span key={`k${parts.length}`} style={{ color: "#86EFAC", fontWeight: 700 }}>{ch}</span>);
    } else {
      buf += ch;
    }
  }
  flush();
  return parts;
}

// Renders verse text word-by-word. Newly-introduced words (the complement
// of the ayah's own `red_words` — words not yet covered in an earlier
// module) are always highlighted, per this module's flipped red-word
// meaning. No hover/click-to-preview — audio only plays via the explicit
// ▶ Ayah / ▶ New Words controls now.
export function renderVerseText(text, newWords, tajweed) {
  const set = new Set(newWords || []);
  const tokens = text.split(/(\s+)/);
  return tokens.map((tok, i) => {
    if (/^\s+$/.test(tok)) return tok;
    const stripped = stripDecorations(tok);
    const isNew = set.has(tok) || (stripped && set.has(stripped));
    return (
      <span key={i} className={`inline-block px-0.5 ${isNew ? "qf-red-word" : ""}`}>
        {tajweed ? tajweedColor(tok) : tok}
      </span>
    );
  });
}

// Every word in an ayah not present in its own red_words list — the
// complement is exactly "new" (not yet covered in an earlier module).
// Standalone waqf/pause/ayah-end marks are never real words, so they're
// excluded even if they'd otherwise pass the "not familiar" check.
export function newWordsInAyah(ayah) {
  if (!ayah?.ar) return [];
  const familiar = new Set(ayah.red_words || []);
  return [...new Set(ayah.ar.split(/\s+/).filter((w) => w && isProperWord(w) && !familiar.has(w)))];
}
